import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  publicSurveyQuery,
  useSubmitPublicSurvey,
  type PublicSurvey,
} from "../components/surveys/api";
import { setLanguage } from "../i18n";
import { ApiError } from "../lib/api";
import { PublicSurveyView } from "../components/surveys/PublicSurveyView";
import { inLang } from "../components/surveys/inLang";
import styles from "../components/surveys/PublicSurvey.module.css";

const LANGS = ["ka", "en"] as const;

/**
 * The page's own frame, so the language switch is present in every state —
 * including the error and already-answered ones, which is where someone is
 * most likely to be struggling to read it.
 *
 * Switches both the words this PAGE owns (the button, yes/no, the validation
 * messages) and the author's own copy, which `inLang` picks per field. A field
 * with no English written falls back to the Georgian rather than blanking.
 */
function Shell({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  return (
    <main className={styles.page}>
      <div className={styles.frame}>
        <div className={styles.langRow}>
          {LANGS.map((lang) => (
            <button
              key={lang}
              type="button"
              // Not `aria-pressed`: this is a set of mutually exclusive
              // choices, and only one is ever current.
              aria-current={i18n.language === lang ? "true" : undefined}
              className={`${styles.langBtn} ${i18n.language === lang ? styles.langOn : ""}`}
              onClick={() => setLanguage(lang)}
            >
              {t(`language.${lang}`)}
            </button>
          ))}
        </div>
        {children}
      </div>
    </main>
  );
}

/**
 * The page a survey recipient lands on from their SMS link.
 *
 * Deliberately outside `_auth`: the recipient is a client, not a CRM user, and
 * the token in the path is the only credential. It shows the survey and nothing
 * about who the link belongs to — the link may have been forwarded, and the API
 * withholds the recipient's identity for exactly that reason.
 */
export const Route = createFileRoute("/s/$token")({
  // `?lang=en`: the SMS went to a member set to English, so the page opens in
  // English (the switch is still there).
  validateSearch: (s: Record<string, unknown>): { lang?: "ka" | "en" } =>
    s.lang === "en" || s.lang === "ka" ? { lang: s.lang } : {},
  component: PublicSurveyPage,
});

function PublicSurveyPage() {
  const { token } = Route.useParams();
  const { lang: linkLang } = Route.useSearch();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (linkLang && linkLang !== i18n.language) setLanguage(linkLang);
    // Once, on landing: a switch the recipient makes afterwards stands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkLang]);
  const lang = i18n.language;
  const { data, isLoading, error } = useQuery(publicSurveyQuery(token));
  const submit = useSubmitPublicSurvey(token);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<PublicSurvey | null>(null);

  if (isLoading) {
    return (
      <Shell>
        <div className={styles.card}>{t("common.loading")}</div>
      </Shell>
    );
  }

  // Invalid, expired and not-yet-open all arrive here identically — the API
  // does not distinguish them, so neither can this page.
  if (error || !data) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <Shell>
        <div className={styles.card}>
          <h1 className={styles.title}>{t("survey.public.unavailableTitle")}</h1>
          <p className={styles.lede}>
            {status === 404 ? t("survey.public.unavailableBody") : t("survey.public.networkError")}
          </p>
        </div>
      </Shell>
    );
  }

  const survey = done ?? data;

  if (survey.already_answered) {
    return (
      <Shell>
        <div className={styles.card}>
          <h1 className={styles.title}>{t("survey.public.thanksTitle")}</h1>
          <p className={styles.lede}>
            {inLang(survey.thank_you_text, survey.thank_you_text_en, lang) ||
              t("survey.public.thanksBody")}
          </p>
        </div>
      </Shell>
    );
  }

  function setAnswer(id: string, value: unknown) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setProblem(null);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setProblem(null);
    submit.mutate(answers, {
      onSuccess: (result) => setDone(result),
      onError: (err) => {
        // The server's message names the offending question ("'How likely…' is
        // required"), which is the difference between fixing the form and
        // giving up on it. Safe to show: the caller already holds a valid link.
        const detail =
          err instanceof ApiError && typeof (err.body as { detail?: unknown })?.detail === "string"
            ? (err.body as { detail: string }).detail
            : t("survey.public.submitError");
        setProblem(detail);
      },
    });
  }

  return (
    <Shell>
      <PublicSurveyView
        survey={survey}
        lang={lang}
        answers={answers}
        onAnswer={setAnswer}
        onSubmit={onSubmit}
        submitting={submit.isPending}
        problem={problem}
      />
    </Shell>
  );
}
